import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntityLinkProps } from './links/useEntityLinkProps';
import {
  Badge,
  Button,
  MessageBarActions,
  Field,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Spinner,
  Switch,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { format } from 'date-fns';
import {
  ExportError,
  useDeleteUpload,
  useExportAccountTransactionsFromUpload,
  useExportBankStatementFromUpload,
  useExtractDr15FromUpload,
  useRestoreUpload,
  useUploadDocument,
  useUploads,
} from '../services/conversionsApi';
import type { FileExportResult } from '../services/conversionsApi';
import type { DocumentCategory, Dr15ExtractionResult, UploadedDocument } from '../types';
import { categoriesFor } from '../lib/documentKinds';

/**
 * Which screen is hosting this panel. It decides **which single conversion each row
 * offers**, and that is the whole point of the prop.
 *
 * <p>CONVERSIONS_SPEC.md §9: *"One flow, one output. No screen offers two different
 * downloads for the same input. The current uploads table's per-row 'Bank statement CSV'
 * + 'Account transactions CSV' pair is exactly the jumble being removed."* Before C7 both
 * buttons sat on the same row on the same screen, so choosing between two Xero formats was
 * a decision pushed onto a bookkeeper mid-task with no context for making it.</p>
 *
 * <p><c>library</c> is the client page's Documents tab: the document list itself, with no
 * conversion buttons at all. Uploads are shared infrastructure, not owned by a flow.</p>
 */
export type UploadsPanelMode = 'bankStatement' | 'accountTransactions' | 'library';

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  fileRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  fileName: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS },
  listSection: {
    marginTop: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionHeading: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalXS,
  },
  emptyState: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  rowActions: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  tableWrap: { overflowX: 'auto' },
});

const CATEGORY_BADGE_COLOR: Record<
  DocumentCategory,
  'brand' | 'danger' | 'important' | 'informative' | 'severe' | 'subtle' | 'success' | 'warning'
> = {
  bankStatement: 'brand',
  dr15Return: 'important',
  payrollReport: 'informative',
  posReport: 'success',
};

type CategoryFilter = DocumentCategory | 'all';

interface Props {
  /** Owned by the host screen — from `?clientId=` on every C7 screen, so it is linkable. */
  clientId: string;
  mode: UploadsPanelMode;
  /** Hidden when the host already shows one (the client page). Avoids two pickers disagreeing. */
  showUploadForm?: boolean;
  onDr15Extracted?: (doc: UploadedDocument, result: Dr15ExtractionResult) => void;
  onDownloaded?: (result: FileExportResult) => void;
}

function triggerDownload(result: FileExportResult) {
  const url = URL.createObjectURL(result.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = result.filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * The categorized upload intake + document list, shared by the two bank-flow screens and
 * the client page's Documents tab (CONVERSIONS_SPEC.md §9).
 *
 * <p>Extracted verbatim out of `ExportPage`'s `UploadsCard` rather than rewritten: the
 * category routing rules, the parse-validation-at-intake behaviour and the handoff
 * semantics are all load-bearing and documented in `docs/categorized-upload-intake.md`.
 * The only behavioural change is <see cref="UploadsPanelMode"/> — which single conversion
 * a row offers.</p>
 */
export default function UploadsPanel({
  clientId,
  mode,
  showUploadForm = true,
  onDr15Extracted,
  onDownloaded,
}: Props) {
  const styles = useStyles();
  const { t } = useTranslation();

  // Only the kinds this section can use (lib/documentKinds): a bank screen offers statements, nothing else.
  const kinds = categoriesFor(mode);
  const oneKind = kinds.length === 1;
  const [category, setCategory] = useState<DocumentCategory>(kinds[0]);
  const [file, setFile] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  // A flow screen lists only the kind it can consume; the library opens on all.
  const [filter, setFilter] = useState<CategoryFilter>(oneKind ? kinds[0] : 'all');
  const [lastUploaded, setLastUploaded] = useState<string | null>(null);
  const [lastDownload, setLastDownload] = useState<string | null>(null);

  // Removed uploads are kept (DP-CNV-BACKUP, ruled 2026-10-26): listed only when asked for, so they can be restored.
  const [showRemoved, setShowRemoved] = useState(false);
  const [lastRemoved, setLastRemoved] = useState<UploadedDocument | null>(null);
  const uploadsQuery = useUploads(clientId, showRemoved);
  const restoreMutation = useRestoreUpload();
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteUpload();
  const bankCsvMutation = useExportBankStatementFromUpload();
  const acctCsvMutation = useExportAccountTransactionsFromUpload();
  const extractDr15Mutation = useExtractDr15FromUpload();

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    setFile(e.target.files?.[0] ?? null);
  }

  function handleUpload() {
    if (!clientId || !file) return;
    setLastUploaded(null);
    uploadMutation.mutate(
      { clientId, category, file },
      {
        onSuccess: (doc) => {
          setLastUploaded(doc.fileName);
          setFile(null);
          setFileInputKey((k) => k + 1); // reset the native file input
        },
      },
    );
  }

  function handleRowExport(doc: UploadedDocument) {
    const mutation = mode === 'accountTransactions' ? acctCsvMutation : bankCsvMutation;
    setLastDownload(null);
    mutation.mutate(
      { clientId: doc.clientId, documentId: doc.id },
      {
        onSuccess: (result) => {
          triggerDownload(result);
          setLastDownload(result.filename);
          onDownloaded?.(result);
        },
      },
    );
  }

  function handleExtractDr15(doc: UploadedDocument) {
    extractDr15Mutation.mutate(
      { clientId: doc.clientId, documentId: doc.id },
      { onSuccess: (result) => onDr15Extracted?.(doc, result) },
    );
  }

  const documents = uploadsQuery.data ?? [];
  const filtered = filter === 'all' ? documents : documents.filter((d) => d.category === filter);

  const uploadError = uploadMutation.error;
  const uploadExportError = uploadError instanceof ExportError ? uploadError : null;
  const actionError =
    bankCsvMutation.error ??
    acctCsvMutation.error ??
    deleteMutation.error ??
    restoreMutation.error ??
    extractDr15Mutation.error;

  function handleRemove(doc: UploadedDocument) {
    setLastRemoved(null);
    deleteMutation.mutate(
      { clientId: doc.clientId, documentId: doc.id },
      { onSuccess: () => setLastRemoved(doc) },
    );
  }

  function handleRestore(doc: Pick<UploadedDocument, 'clientId' | 'id'>) {
    setLastRemoved(null);
    restoreMutation.mutate({ clientId: doc.clientId, documentId: doc.id });
  }
  const actionExportError = actionError instanceof ExportError ? actionError : null;
  const anyActionPending =
    bankCsvMutation.isPending ||
    acctCsvMutation.isPending ||
    deleteMutation.isPending ||
    extractDr15Mutation.isPending;

  if (!clientId) {
    return <Text className={styles.emptyState}>{t('uploads.loadPrompt')}</Text>;
  }

  return (
    <div className={styles.form}>
      {uploadError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {uploadExportError
              ? `${t('uploads.errors.upload')}: ${uploadExportError.serverError}`
              : t('uploads.errors.transport')}
          </MessageBarBody>
        </MessageBar>
      )}
      {actionError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {actionExportError
              ? `${t('uploads.errors.action')}: ${actionExportError.serverError}`
              : t('uploads.errors.transport')}
          </MessageBarBody>
        </MessageBar>
      )}
      {lastUploaded && !uploadMutation.isPending && !uploadError && (
        <MessageBar intent="success">
          <MessageBarBody>
            {t('uploads.uploadSuccessPrefix')}: {lastUploaded}
          </MessageBarBody>
        </MessageBar>
      )}
      {lastDownload && !anyActionPending && !actionError && (
        <MessageBar intent="success">
          <MessageBarBody>
            {t('uploads.downloadSuccessPrefix')}: {lastDownload}
          </MessageBarBody>
        </MessageBar>
      )}

      {showUploadForm && (
        <>
          {!oneKind && (
            <Field label={t('uploads.categoryLabel')}>
              <RadioGroup
                layout="horizontal"
                value={category}
                onChange={(_e, data) => setCategory(data.value as DocumentCategory)}
              >
                {kinds.map((value) => (
                  <Radio key={value} value={value} label={t(`uploads.categories.${value}`)} />
                ))}
              </RadioGroup>
            </Field>
          )}

          <Field
            label={t('uploads.fileLabel')}
            hint={
              category === 'bankStatement'
                ? t('uploads.fileHintBank')
                : category === 'dr15Return'
                  ? t('uploads.fileHintDr15')
                  : t('uploads.fileHintReference')
            }
          >
            <div className={styles.fileRow}>
              <input key={fileInputKey} type="file" onChange={handleFileChange} />
            </div>
          </Field>

          <div className={styles.actions}>
            <Button
              appearance="primary"
              onClick={handleUpload}
              disabled={uploadMutation.isPending || !file}
              icon={uploadMutation.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {uploadMutation.isPending ? t('uploads.uploading') : t('uploads.uploadButton')}
            </Button>
          </div>
        </>
      )}

      <div className={styles.listSection}>
        <Text as="h3" className={styles.sectionHeading}>
          {t('uploads.listHeading')}
        </Text>

        {lastRemoved && (
          <MessageBar intent="info">
            <MessageBarBody>
              {t('uploads.removed.notice', { file: lastRemoved.fileName })}{' '}
              <Button size="small" appearance="transparent" onClick={() => handleRestore(lastRemoved)}>
                {t('uploads.removed.undo')}
              </Button>
            </MessageBarBody>
          </MessageBar>
        )}
        <Switch
          checked={showRemoved}
          onChange={(_e, data) => setShowRemoved(data.checked)}
          label={t('uploads.removed.show')}
        />

        {!oneKind && (
          <TabList selectedValue={filter} onTabSelect={(_e, data) => setFilter(data.value as CategoryFilter)}>
            <Tab value="all">{t('uploads.filters.all')}</Tab>
            {kinds.map((value) => (
              <Tab key={value} value={value}>
                {t(`uploads.categories.${value}`)}
              </Tab>
            ))}
          </TabList>
        )}

        {uploadsQuery.isLoading ? (
          <Spinner size="tiny" label={t('common.loading')} />
        ) : uploadsQuery.isError ? (
          // P6 (the polish bar): an error offers a retry, never a dead end.
          <MessageBar intent="error">
            <MessageBarBody>{t('uploads.errors.list')}</MessageBarBody>
            <MessageBarActions>
              <Button size="small" onClick={() => void uploadsQuery.refetch()}>{t('common.retry')}</Button>
            </MessageBarActions>
          </MessageBar>
        ) : filtered.length === 0 ? (
          <Text className={styles.emptyState}>{oneKind ? t('uploads.emptyStatements') : t('uploads.empty')}</Text>
        ) : (
          <div className={styles.tableWrap}>
            <Table size="small" aria-label={t('uploads.listHeading')}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t('uploads.columns.file')}</TableHeaderCell>
                  <TableHeaderCell>{t('uploads.columns.category')}</TableHeaderCell>
                  <TableHeaderCell>{t('uploads.columns.uploaded')}</TableHeaderCell>
                  <TableHeaderCell>{t('uploads.columns.transactions')}</TableHeaderCell>
                  <TableHeaderCell>{t('uploads.columns.actions')}</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((doc) => (
                  <UploadRow
                    key={doc.id}
                    doc={doc}
                    mode={mode}
                    onConvert={() => handleRowExport(doc)}
                    onExtractDr15={() => handleExtractDr15(doc)}
                    onDelete={() => handleRemove(doc)}
                    onRestore={() => handleRestore(doc)}
                    restorePending={
                      restoreMutation.isPending && restoreMutation.variables?.documentId === doc.id
                    }
                    convertPending={
                      (mode === 'accountTransactions' ? acctCsvMutation : bankCsvMutation).isPending &&
                      (mode === 'accountTransactions' ? acctCsvMutation : bankCsvMutation).variables
                        ?.documentId === doc.id
                    }
                    extractPending={
                      extractDr15Mutation.isPending && extractDr15Mutation.variables?.documentId === doc.id
                    }
                    deletePending={
                      deleteMutation.isPending && deleteMutation.variables?.documentId === doc.id
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function UploadRow({
  doc,
  mode,
  onConvert,
  onExtractDr15,
  onDelete,
  onRestore,
  convertPending,
  extractPending,
  deletePending,
  restorePending,
}: {
  doc: UploadedDocument;
  mode: UploadsPanelMode;
  onConvert: () => void;
  onExtractDr15: () => void;
  onDelete: () => void;
  onRestore: () => void;
  convertPending: boolean;
  extractPending: boolean;
  deletePending: boolean;
  restorePending: boolean;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  // §9: "Handoffs get more explicit, not less." A POS or payroll report OPENS the manual journal for it, with the
  // client and the document carried over. A real link (fleet ruling 2026-10-29): right-click opens it in a new tab.
  const manualJournalLink = useEntityLinkProps(
    `/journals?${new URLSearchParams({ clientId: doc.clientId, sourceDocumentId: doc.id }).toString()}`,
  );
  const isBank = doc.category === 'bankStatement';
  const isDr15 = doc.category === 'dr15Return';

  return (
    <TableRow>
      <TableCell>
        <Text className={styles.fileName} title={doc.fileName}>
          {doc.fileName}
        </Text>
      </TableCell>
      <TableCell>
        <Badge appearance="tint" color={CATEGORY_BADGE_COLOR[doc.category]}>
          {t(`uploads.categories.${doc.category}`)}
        </Badge>
      </TableCell>
      <TableCell>{format(new Date(doc.uploadedAt), 'yyyy-MM-dd')}</TableCell>
      <TableCell>{isBank ? doc.transactionCount ?? '—' : t('uploads.referenceDocument')}</TableCell>
      <TableCell>
        {doc.isDeleted ? (
          // Removed (kept): it says who removed it and when, and offers only Restore, never an action that uses it.
          <div className={styles.rowActions}>
            <Text>
              {t('uploads.removed.by', {
                name: doc.deletedByName ?? t('uploads.removed.someone'),
                date: doc.deletedAt ? format(new Date(doc.deletedAt), 'yyyy-MM-dd') : '',
              })}
            </Text>
            <Button
              size="small"
              onClick={onRestore}
              disabled={restorePending}
              icon={restorePending ? <Spinner size="tiny" /> : undefined}
            >
              {t('uploads.removed.restore')}
            </Button>
          </div>
        ) : (
        <div className={styles.rowActions}>
          {/*
            ⚠ EXACTLY ONE conversion button, and only on the flow screens (§9). The library
            offers none: it is the document list, not a conversion surface. Restoring a
            second download here would restore the jumble C7 exists to remove.
          */}
          {isBank && mode !== 'library' && (
            <Button
              size="small"
              appearance="primary"
              onClick={onConvert}
              disabled={convertPending}
              icon={convertPending ? <Spinner size="tiny" /> : undefined}
            >
              {mode === 'accountTransactions'
                ? t('uploads.actions.accountTransactionsCsv')
                : t('uploads.actions.bankStatementCsv')}
            </Button>
          )}
          {isDr15 && (
            <Button
              size="small"
              onClick={onExtractDr15}
              disabled={extractPending}
              icon={extractPending ? <Spinner size="tiny" /> : undefined}
            >
              {t('uploads.actions.extractDr15')}
            </Button>
          )}
          {!isBank && !isDr15 && (
            <Button as="a" size="small" {...manualJournalLink}>
              {t('uploads.actions.prepareManualJournal')}
            </Button>
          )}
          {/*
            View document (2026-10-29): a real link (RL1) to the screen that opens the file, in its own tab so the list
            stays where it was. That screen asks for a short-lived read-only link and hands the tab to it.
          */}
          <Button
            as="a"
            size="small"
            appearance="subtle"
            href={`/uploads/view?${new URLSearchParams({ clientId: doc.clientId, documentId: doc.id }).toString()}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('uploads.actions.viewOf', { name: doc.fileName })}
          >
            {t('uploads.actions.view')}
          </Button>
          <Button
            size="small"
            appearance="subtle"
            onClick={onDelete}
            disabled={deletePending}
            icon={deletePending ? <Spinner size="tiny" /> : undefined}
          >
            {t('uploads.actions.delete')}
          </Button>
        </div>
        )}
      </TableCell>
    </TableRow>
  );
}
